/**
 * delete-refusal — the one surface a catalog delete refuses through. Event,
 * ticket type and location each have their own door (`deleteEvent`,
 * `deleteTicketType`, `deleteLocation`), and each door answers a refusal the
 * same way: a `Dependents` census of what still names the document. This
 * element is where that census becomes something an operator can act on —
 * the counts as they stand, and the remedy that actually exists for this
 * entity, never a bare "cannot delete".
 *
 * The remedy is entity-specific because the product has a different real
 * gesture for each: an event archives (`Event.status: 'archived'` already
 * exists), a ticket type closes its sales window (`TicketTypeSchema` carries
 * no archived state, so the window IS the gesture), and a location is
 * released by pointing whatever names it somewhere else. That entity-level
 * gesture is the only clause stated unconditionally; everything else a remedy
 * has to say belongs to one census row and renders under that row alone, so
 * the operator is never sent after a blocker this refusal just reported the
 * absence of.
 *
 * A named element rather than an inline `show(() => html`<div>…`)`: an
 * anonymous root reports as the tag `div`, which names nothing and cannot
 * anchor a live assertion — and this surface is exactly the one a live
 * oracle keys on to tell "asked to confirm" from "was refused".
 *
 * Content-only, per schmancy's overlay contract: no surface of its own, no
 * full-bleed fill, no `:host { display: block }` — the overlay supplies the
 * material and blockifies the mount — so it pads itself and nothing else.
 *
 * Which tier it lands in is not this element's to know. `show()` reads the
 * last tracked gesture inside a 750 ms window, and a refusal arrives on a
 * server answer whose latency decides whether that window is still open — so
 * the same refusal renders anchored on a fast answer and as a sheet on a
 * slow one. The measure is therefore a definite one on the root
 * (`w-md max-w-full`), which sizes correctly under the anchored surface's
 * shrink-to-fit `w-max` AND yields to the sheet surface's own already-definite
 * width; a percentage would have nothing to resolve against in the first and
 * a `:host` pin would leave dead glass in the second.
 *
 * It carries its own named dismiss button: this is the terminal answer to a
 * destructive action, and the anchored tier draws no backdrop, so the button
 * is the visible way out of a multi-sentence remedy the operator is meant to
 * read and act on. Its three call sites open it `sticky`, which is what
 * keeps a stray pointerdown from closing it mid-read and announces it as a
 * dialog on either tier.
 *
 * The census can legitimately arrive all-zero: a refusal turns on existence
 * inside the deleting transaction, while the numbers are `count()`
 * aggregations read afterwards, and a failed aggregation degrades the
 * message rather than the guard. So an empty row list is a real state with
 * its own line, never an empty panel.
 */
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { Dependents } from "@momo/types";
import { html, type TemplateResult } from "lit";
import { property } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";
import { when } from "lit/directives/when.js";
import { LocalizeMixin } from "../i18n/localize.ts";

/** Which door refused — the discriminant that selects both the copy and
 *  which census rows this entity can even have. */
export type CatalogEntity = "event" | "ticketType" | "location";

/**
 * `as const satisfies` rather than a plain annotation: `satisfies` checks
 * every `field` really is a census row while `as const` keeps each key a
 * string literal, which is what makes `this.t(...)` type-check the key
 * against the live catalog. An annotation alone widens them to `string` and
 * the keys stop being checked at all.
 *
 * A row absent from an entity is one its own door can never report — an
 * event has no child locations, a location has no tickets — so it could only
 * ever be the schema's own 0.
 */
const REFUSAL = {
	event: {
		title: "admin.events.form.deleteRefusalTitle",
		lead: "admin.events.form.deleteRefusalLead",
		remedy: "admin.events.form.deleteRefusalRemedy",
		rows: [
			{
				field: "orders",
				one: "admin.events.form.deleteRefusalOrdersOne",
				other: "admin.events.form.deleteRefusalOrdersOther",
				remedy: "admin.events.form.deleteRefusalOrdersRemedy",
			},
			{
				field: "tickets",
				one: "admin.events.form.deleteRefusalTicketsOne",
				other: "admin.events.form.deleteRefusalTicketsOther",
				remedy: "admin.events.form.deleteRefusalTicketsRemedy",
			},
			{
				field: "openCheckouts",
				one: "admin.events.form.deleteRefusalCheckoutsOne",
				other: "admin.events.form.deleteRefusalCheckoutsOther",
				remedy: "admin.events.form.deleteRefusalCheckoutsRemedy",
			},
			{
				field: "spendingCampaigns",
				one: "admin.events.form.deleteRefusalCampaignsOne",
				other: "admin.events.form.deleteRefusalCampaignsOther",
				remedy: "admin.events.form.deleteRefusalCampaignsRemedy",
			},
		],
	},
	ticketType: {
		title: "admin.ticketTypes.form.deleteRefusalTitle",
		lead: "admin.ticketTypes.form.deleteRefusalLead",
		remedy: "admin.ticketTypes.form.deleteRefusalRemedy",
		rows: [
			{
				field: "tickets",
				one: "admin.ticketTypes.form.deleteRefusalTicketsOne",
				other: "admin.ticketTypes.form.deleteRefusalTicketsOther",
				remedy: "admin.ticketTypes.form.deleteRefusalTicketsRemedy",
			},
			{
				field: "consumed",
				one: "admin.ticketTypes.form.deleteRefusalConsumedOne",
				other: "admin.ticketTypes.form.deleteRefusalConsumedOther",
				remedy: "admin.ticketTypes.form.deleteRefusalConsumedRemedy",
			},
		],
	},
	location: {
		title: "admin.locations.form.deleteRefusalTitle",
		lead: "admin.locations.form.deleteRefusalLead",
		remedy: "admin.locations.form.deleteRefusalRemedy",
		rows: [
			{
				field: "events",
				one: "admin.locations.form.deleteRefusalEventsOne",
				other: "admin.locations.form.deleteRefusalEventsOther",
				remedy: "admin.locations.form.deleteRefusalEventsRemedy",
			},
			{
				field: "ticketTypes",
				one: "admin.locations.form.deleteRefusalTicketTypesOne",
				other: "admin.locations.form.deleteRefusalTicketTypesOther",
				remedy: "admin.locations.form.deleteRefusalTicketTypesRemedy",
			},
			{
				field: "childLocations",
				one: "admin.locations.form.deleteRefusalChildLocationsOne",
				other: "admin.locations.form.deleteRefusalChildLocationsOther",
				remedy: "admin.locations.form.deleteRefusalChildLocationsRemedy",
			},
		],
	},
} as const satisfies Record<
	CatalogEntity,
	{
		readonly title: string;
		readonly lead: string;
		readonly remedy: string;
		readonly rows: readonly {
			readonly field: keyof Dependents.Dependents;
			readonly one: string;
			readonly other: string;
			readonly remedy: string;
		}[];
	}
>;

@schmancyElement("delete-refusal")
export class DeleteRefusalView extends LocalizeMixin(SchmancyElement) {
	@property({ type: String }) entity: CatalogEntity = "event";
	/** The census as the door reported it — or as much of it as the form
	 *  could already see locally, which is why every row defaults to 0 and
	 *  only non-zero rows are ever rendered. */
	@property({ attribute: false }) dependents: Dependents.Dependents =
		Dependents.DependentsSchema.parse({});

	override render(): TemplateResult {
		const copy = REFUSAL[this.entity];
		// Each surviving row carries its own count, the key that count already
		// selected, and the one remedy clause that answers this row — so the
		// template states one fact per line and picks nothing, and a clause
		// naming a blocker this census reports as absent is unreachable.
		const standing = copy.rows.flatMap((r) => {
			const count = this.dependents[r.field];
			return count > 0
				? [
						{
							field: r.field,
							key: count === 1 ? r.one : r.other,
							remedy: r.remedy,
							count,
						},
					]
				: [];
		});
		return html`
			<div class="mx-auto grid w-md max-w-full gap-5 p-6">
				<schmancy-typography type="headline" token="sm">${this.t(copy.title)}</schmancy-typography>
				<schmancy-typography type="body" token="md">${this.t(copy.lead)}</schmancy-typography>
				${when(
					standing.length > 0,
					() => html`
						<schmancy-list surface="transparent">
							${repeat(
								standing,
								(r) => r.field,
								(r) => html`
									<schmancy-list-item readonly rounded>
										<schmancy-icon slot="leading" class="text-error-default">block</schmancy-icon>
										<schmancy-typography type="body" token="md">
											${this.t(r.key, { count: r.count })}
										</schmancy-typography>
										<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
											${this.t(r.remedy)}
										</schmancy-typography>
									</schmancy-list-item>
								`,
							)}
						</schmancy-list>
					`,
					() => html`
						<schmancy-typography type="body" token="md" class="text-surface-onVariant">
							${this.t("admin.common.deleteRefusalCountsUnknown")}
						</schmancy-typography>
					`,
				)}
				<schmancy-typography type="body" token="md">${this.t(copy.remedy)}</schmancy-typography>
				<div class="flex justify-end">
					<schmancy-button
						variant="filled"
						@click=${() =>
							this.dispatchEvent(
								new CustomEvent("close", { bubbles: true, composed: true }),
							)}
					>${this.t("admin.common.close")}</schmancy-button>
				</div>
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"delete-refusal": DeleteRefusalView;
	}
}
